import type { InAppBrowserApp, Platform, InAppBrowserInfo } from './inAppBrowser.types';

// Conservative, specific patterns. Per team guidance: bias toward false negatives —
// never match plain Chrome/Safari.
const IN_APP_PATTERNS: ReadonlyArray<{ app: InAppBrowserApp; test: RegExp }> = [
  { app: 'facebook', test: /\b(FBAN|FBAV|FB_IAB)\b/ },
  { app: 'messenger', test: /\bMessenger\b/ },
  { app: 'instagram', test: /\bInstagram\b/ },
  { app: 'whatsapp', test: /\bWhatsApp\b/ },
  { app: 'line', test: /\bLine\/[\d.]+/ },
  { app: 'telegram', test: /\bTelegram(Bot)?\b/ },
  { app: 'wechat', test: /\bMicroMessenger\b/ },
];

export const detectPlatform = (userAgent: string): Platform => {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'ios';
  }
  if (/Android/i.test(userAgent)) {
    return 'android';
  }
  return 'other';
};

export const detectInAppBrowser = (userAgent: string | undefined | null): InAppBrowserInfo => {
  if (!userAgent) {
    return { isInAppBrowser: false, app: null, platform: 'other' };
  }
  const platform = detectPlatform(userAgent);
  const match = IN_APP_PATTERNS.find(({ test }) => test.test(userAgent));
  return { isInAppBrowser: !!match, app: match?.app ?? null, platform };
};

export const buildAndroidIntentUrl = (url: string): string => {
  const parsed = new URL(url);
  const scheme = parsed.protocol.replace(':', '');
  const hostAndPath = `${parsed.host}${parsed.pathname}${parsed.search}`;
  const fallback = encodeURIComponent(url);
  return `intent://${hostAndPath}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${fallback};end;`;
};

/**
 * Android: deep-link the current URL into Chrome via an intent.
 * iOS/other: there is no reliable programmatic "open in Safari" from an in-app browser,
 * so open the native share sheet (Web Share API) where the user can choose
 * "Open in Safari" / "Open in Browser". Falls back to copying the link when Web Share
 * is unavailable (older iOS / restricted webviews).
 *
 * Must be called from within a user gesture (e.g. an onClick handler) so the share
 * sheet's transient-activation requirement is satisfied.
 */
const copyLink = (url: string, onCopied?: () => void): void => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText(url)
      .then(() => onCopied?.())
      .catch(() => undefined);
  }
};

export const openInExternalBrowser = (info: InAppBrowserInfo, url: string, onCopied?: () => void): void => {
  if (info.platform === 'android') {
    if (typeof window !== 'undefined') {
      window.location.href = buildAndroidIntentUrl(url);
    }
    return;
  }
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    navigator.share({ url }).catch((err: unknown) => {
      // User dismissed the share sheet — leave them where they are.
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // share() exists but failed (e.g. restricted in-app webview) — fall back to copying.
      copyLink(url, onCopied);
    });
    return;
  }
  copyLink(url, onCopied);
};
